/**
 * Fall-KI-Berater Chat Komponente
 * Claude Sonnet 4.6 powered Chat pro Betreuungsfall
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Bot, Send, Loader2, Plus, Clock, FileText, Paperclip, X,
  CheckCircle, AlertTriangle, CalendarClock, Sparkles,
  MessageSquarePlus, ChevronDown, Shield, Briefcase
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function FallKiChat({ klientId, klientName }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [sessions, setSessions] = useState([]);
  const [modus, setModus] = useState('allgemein');
  const [dateien, setDateien] = useState([]);
  const [kontextInfo, setKontextInfo] = useState(null);
  const [creatingTask, setCreatingTask] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Sessions laden
  useEffect(() => {
    if (!klientId) return;
    loadSessions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [klientId]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fall-ki/sessions/${klientId}`);
      const data = await res.json();
      if (data.success) setSessions(data.sessions || []);
    } catch {}
  };

  const loadSession = async (sid) => {
    try {
      const res = await fetch(`${API_URL}/api/fall-ki/history/${klientId}?session_id=${sid}`);
      const data = await res.json();
      if (data.success && data.sessions?.length > 0) {
        setMessages(data.sessions[0].messages || []);
        setSessionId(sid);
      }
    } catch {
      toast.error('Fehler beim Laden des Chats');
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setSessionId('');
    setDateien([]);
    setKontextInfo(null);
  };

  const handleSend = async () => {
    if ((!input.trim() && dateien.length === 0) || loading) return;
    const text = input.trim() || '(Dokument hochgeladen - bitte analysieren)';
    setInput('');

    setMessages(prev => [...prev, { rolle: 'user', text, modus, hat_dokumente: dateien.length > 0 }]);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('nachricht', text);
      formData.append('klient_id', klientId);
      formData.append('session_id', sessionId);
      formData.append('modus', modus);
      dateien.forEach(f => formData.append('dateien', f));

      const res = await fetch(`${API_URL}/api/fall-ki/chat`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();

      if (data.success) {
        if (!sessionId && data.session_id) setSessionId(data.session_id);
        setMessages(prev => [...prev, {
          rolle: 'assistent',
          text: data.antwort,
          modus,
          erkannte_aufgaben: data.erkannte_aufgaben || [],
          erkannte_fristen: data.erkannte_fristen || [],
        }]);
        if (data.kontext_info) setKontextInfo(data.kontext_info);
        // Sessions aktualisieren
        loadSessions();
      } else {
        setMessages(prev => [...prev, { rolle: 'assistent', text: data.antwort || 'Fehler', fehler: true }]);
      }
    } catch (e) {
      setMessages(prev => [...prev, { rolle: 'assistent', text: `Fehler: ${e.message}`, fehler: true }]);
    } finally {
      setLoading(false);
      setDateien([]);
    }
  };

  const handleCreateTask = async (aufgabe) => {
    setCreatingTask(aufgabe.titel);
    try {
      const formData = new FormData();
      formData.append('klient_id', klientId);
      formData.append('titel', aufgabe.titel);
      formData.append('prioritaet', aufgabe.prioritaet || 'mittel');
      formData.append('frist', aufgabe.frist || '');
      formData.append('beschreibung', `KI-Empfehlung für ${klientName}`);

      const res = await fetch(`${API_URL}/api/fall-ki/create-aufgabe`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Aufgabe "${aufgabe.titel}" erstellt`);
      } else {
        toast.error('Fehler beim Erstellen');
      }
    } catch {
      toast.error('Fehler');
    } finally {
      setCreatingTask(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const removeFile = (idx) => setDateien(prev => prev.filter((_, i) => i !== idx));

  const MODI = [
    { value: 'allgemein', label: 'Allgemein', icon: Bot },
    { value: 'analyse', label: 'Analyse', icon: FileText },
    { value: 'brief', label: 'Brief erstellen', icon: Briefcase },
    { value: 'bericht', label: 'Jahresbericht', icon: Shield },
  ];

  return (
    <div className="flex h-[calc(100vh-300px)] min-h-[500px] gap-4" data-testid="fall-ki-chat">
      {/* Sidebar: Sessions */}
      <div className="w-56 shrink-0 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden">
        <div className="p-3 border-b flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700">Chats</span>
          <button onClick={handleNewChat}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" title="Neuer Chat"
            data-testid="fall-ki-new-chat">
            <MessageSquarePlus className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {sessions.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-4">Noch keine Chats</p>
          )}
          {sessions.map(s => (
            <button key={s.session_id}
              onClick={() => loadSession(s.session_id)}
              className={`w-full text-left px-2.5 py-2 rounded-lg text-xs transition-colors ${
                sessionId === s.session_id ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'hover:bg-slate-50 text-slate-600'
              }`}
              data-testid={`fall-ki-session-${s.session_id}`}>
              <div className="font-medium truncate">{s.titel}</div>
              <div className="text-slate-400 mt-0.5">{s.nachrichten_count} Nachrichten</div>
            </button>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b bg-gradient-to-r from-violet-50 to-blue-50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">Claude KI-Berater</p>
              <p className="text-xs text-slate-500">Sonnet 4.6 &middot; Pseudonymisiert</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {kontextInfo && (
              <div className="flex gap-1.5">
                <Badge variant="secondary" className="text-[10px]">{kontextInfo.aufgaben} Aufgaben</Badge>
                <Badge variant="secondary" className="text-[10px]">{kontextInfo.massnahmen} Maßnahmen</Badge>
                <Badge variant="secondary" className="text-[10px]">{kontextInfo.dokumente} Dokumente</Badge>
              </div>
            )}
          </div>
        </div>

        {/* Sicherheits-Banner */}
        <div className="px-4 py-1.5 text-xs flex items-center gap-1.5 bg-green-50 text-green-700 border-b shrink-0">
          <Shield className="w-3 h-3" />
          Keine Klarnamen werden an Claude gesendet &middot; Nur pseudonymisierte Falldaten
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <Sparkles className="w-12 h-12 mx-auto text-violet-300 mb-4" />
              <p className="text-lg font-semibold text-slate-700">KI-Berater für {klientName}</p>
              <p className="text-sm text-slate-500 mt-1 mb-6">Claude analysiert den gesamten Fall und berät Sie.</p>
              <div className="grid grid-cols-2 gap-2 max-w-md mx-auto">
                {[
                  { text: 'Was sind die nächsten Schritte?', m: 'analyse' },
                  { text: 'Erstelle einen Jahresbericht', m: 'bericht' },
                  { text: 'Formuliere einen Brief an das Gericht', m: 'brief' },
                  { text: 'Welche Fristen laufen bald ab?', m: 'analyse' },
                ].map(q => (
                  <button key={q.text}
                    onClick={() => { setModus(q.m); setInput(q.text); }}
                    className="text-left text-xs px-3 py-2.5 bg-violet-50 hover:bg-violet-100 rounded-lg text-violet-700 transition-colors border border-violet-200"
                    data-testid={`fall-ki-suggestion-${q.m}`}>
                    {q.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.rolle === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
                msg.rolle === 'user'
                  ? 'bg-violet-600 text-white rounded-br-md'
                  : msg.fehler
                    ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-md'
                    : 'bg-slate-50 text-slate-800 border border-slate-200 rounded-bl-md'
              }`}>
                {msg.rolle === 'assistent' && (
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Sparkles className="w-3 h-3 text-violet-500" />
                    <span className="text-[10px] font-medium text-violet-600">Claude</span>
                    {msg.modus && msg.modus !== 'allgemein' && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">{msg.modus}</Badge>
                    )}
                  </div>
                )}
                <div className="whitespace-pre-wrap break-words leading-relaxed">
                  {msg.text}
                </div>

                {/* Erkannte Aufgaben */}
                {msg.erkannte_aufgaben?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                    <p className="text-xs font-semibold text-violet-700 flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Vorgeschlagene Aufgaben:
                    </p>
                    {msg.erkannte_aufgaben.map((a, j) => (
                      <div key={j} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-violet-200">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-700 truncate">{a.titel}</p>
                          <div className="flex gap-1.5 mt-0.5">
                            <Badge variant="outline" className="text-[10px]">{a.prioritaet}</Badge>
                            {a.frist && <Badge variant="outline" className="text-[10px]"><CalendarClock className="w-2.5 h-2.5 mr-0.5" />{a.frist}</Badge>}
                          </div>
                        </div>
                        <Button size="sm" variant="outline"
                          className="ml-2 shrink-0 text-xs h-7 px-2"
                          onClick={() => handleCreateTask(a)}
                          disabled={creatingTask === a.titel}
                          data-testid={`fall-ki-create-task-${j}`}>
                          {creatingTask === a.titel ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                          Erstellen
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Erkannte Fristen */}
                {msg.erkannte_fristen?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {msg.erkannte_fristen.map((f, j) => (
                      <Badge key={j} className="bg-amber-100 text-amber-800 text-[10px]">
                        <CalendarClock className="w-3 h-3 mr-1" />{f}
                      </Badge>
                    ))}
                  </div>
                )}

                {msg.hat_dokumente && msg.rolle === 'user' && (
                  <div className="mt-1.5 flex items-center gap-1 text-violet-200 text-xs">
                    <Paperclip className="w-3 h-3" /> Dokument angehängt
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-violet-50 border border-violet-200 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
                <span className="text-xs text-violet-600">Claude analysiert den Fall...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Datei-Vorschau */}
        {dateien.length > 0 && (
          <div className="px-4 py-2 border-t bg-slate-50 flex gap-2 flex-wrap shrink-0">
            {dateien.map((f, i) => (
              <div key={i} className="flex items-center gap-1.5 bg-white rounded-lg px-2.5 py-1.5 border text-xs">
                <FileText className="w-3.5 h-3.5 text-blue-500" />
                <span className="max-w-[120px] truncate text-slate-700">{f.name}</span>
                <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="border-t bg-white px-4 py-3 shrink-0">
          <input ref={fileInputRef} type="file" multiple className="hidden"
            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
            onChange={e => { setDateien(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; }}
          />

          <div className="flex items-end gap-2">
            {/* Modus-Auswahl */}
            <Select value={modus} onValueChange={setModus}>
              <SelectTrigger className="w-[130px] h-[38px] text-xs shrink-0" data-testid="fall-ki-modus">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODI.map(m => (
                  <SelectItem key={m.value} value={m.value} className="text-xs">
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Datei-Upload */}
            <button onClick={() => fileInputRef.current?.click()}
              className="shrink-0 h-[38px] w-[38px] rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 flex items-center justify-center"
              title="Dokument hochladen" data-testid="fall-ki-upload-btn">
              <Paperclip className="w-4 h-4" />
            </button>

            <Textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
              placeholder="Frage zum Fall stellen..."
              rows={1} className="resize-none text-sm min-h-[38px] max-h-[100px]"
              data-testid="fall-ki-input" />

            <Button size="sm" onClick={handleSend}
              disabled={(!input.trim() && dateien.length === 0) || loading}
              className="shrink-0 h-[38px] w-[38px] p-0 bg-violet-600 hover:bg-violet-700"
              data-testid="fall-ki-send-btn">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
